import React from "react";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";

/**
 * Props:
 * - label: string
 * - accept: string | object (react-dropzone accept)
 * - maxSizeBytes?: number (default 2MB)
 * - autoRemove?: boolean
 * - multiple?: boolean (default true)
 * - url?: string (relative endpoint, e.g. "files/upload/..." OR "/files/upload/...")
 * - uuid?: string
 * - category?: string
 * - onGetUrl?: () => (string | Promise<string>)  // may return relative or absolute
 * - onFileUpload?: (responseJson) => void
 * - fieldName?: string (default "file")
 */
export default function DropzoneImages(props) {
  const [uploading, setUploading] = React.useState(false);
  const [status, setStatus] = React.useState("");
  const [error, setError] = React.useState("");
  const [pickedFile, setPickedFile] = React.useState(null);

  const maxSize = props.maxSizeBytes ?? 1048576 * 2; // 2MB

  const normalizeAccept = (a) => {
    if (!a) return undefined;

    // If already object (newer react-dropzone style), use as-is
    if (typeof a === "object") return a;

    // If string like "application/pdf" or "application/pdf,image/*"
    if (typeof a === "string") {
      const items = a
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      // Convert to accept object form: { "application/pdf": [] }
      const obj = {};
      items.forEach((k) => (obj[k] = []));
      return obj;
    }

    return undefined;
  };

  const isAbsoluteUrl = (u) => /^https?:\/\//i.test(String(u || ""));

  const resolveUploadUrl = async () => {
    // If caller provides a function, prefer it
    if (typeof props.onGetUrl === "function") {
      const u = await props.onGetUrl();
      if (u) return u;
    }

    // If caller provides url prop, use it
    if (props.url) return props.url;

    // fallback route format (relative)
    return `files/upload/${props.category}/${props.uuid}`;
  };

  const uploadFile = async (file, manageState = true) => {
    if (manageState) {
      setError("");
      setStatus("");
      setUploading(true);
    }

    try {
      let url = await resolveUploadUrl();
      if (!isAbsoluteUrl(url)) url = String(url).replace(/^\//, "");

      const fd = new FormData();
      fd.append(props.fieldName ?? "file", file);

      const res = await api.post(url, fd);

      const json = res?.data;

      if (manageState) {
        setStatus("Uploaded");
        if (typeof props.onFileUpload === "function") props.onFileUpload(json);
      }

      return json;
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Upload failed";

      setError(msg);
      throw e;
    } finally {
      if (manageState) setUploading(false);
    }
  };

  const uploadFiles = async (files) => {
    setError("");
    setStatus("");
    setUploading(true);

    try {
      const results = [];

      for (const file of files) {
        results.push(await uploadFile(file, false));
      }

      setStatus(`Uploaded ${results.length} file${results.length === 1 ? "" : "s"}`);

      if (typeof props.onFilesUpload === "function") {
        props.onFilesUpload(results);
      }

      if (results.length === 1 && typeof props.onFileUpload === "function") {
        props.onFileUpload(results[0]);
      }

      if (props.autoRemove === true) {
        setPickedFile(null);
        setStatus("");
      }
    } finally {
      setUploading(false);
    }
  };

  const onDrop = React.useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      setPickedFile(acceptedFiles.length === 1 ? acceptedFiles[0] : null);
      uploadFiles(acceptedFiles);
    },
    [props.url, props.uuid, props.category]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: props.multiple !== false,
    maxSize,
    accept: normalizeAccept(props.accept),
    onDrop,
    onDropRejected: (rejections) => {
      const r = rejections?.[0];
      const msg = r?.errors?.[0]?.message || "File rejected (type/size).";
      setError(msg);
    },
  });

  const label = props.label ?? "Upload file";

  return (
    <div>
      <div
        {...getRootProps()}
        style={{
          border: "2px dashed #cbd5e1",
          borderRadius: 12,
          padding: 18,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <input {...getInputProps()} />
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {isDragActive ? "Drop it here…" : label}
        </div>

        <div style={{ opacity: 0.8, fontSize: 13 }}>
          {uploading
            ? "Uploading…"
            : pickedFile
              ? `Selected: ${pickedFile.name}`
              : "Drag & drop or click to choose a file."}
        </div>

        {status ? (
          <div style={{ marginTop: 10, fontSize: 13 }}>{status}</div>
        ) : null}

        {error ? (
          <div style={{ marginTop: 10, color: "#b91c1c", fontSize: 13 }}>
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
